import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import { roleCanPerform } from '../utils/workspacePermissions'
import { useDisplayName } from './useDisplayName'
import { firestoreDb } from '../services/firebase'
import { scopedReadConstraints } from '../services/accessScopeService'
import { isNoSignInPreviewActive } from '../debug'
import type { DocumentLock } from '../types/schema'
import { isHelperOnlyAccount } from '../utils/helperMembership'

const EMPTY_LOCKS: DocumentLock[] = []
const LOCK_LEASE_MS = 60_000
/** Edit-session lease for the node detail drawer: refreshed on each edit
 * interaction (and every 30s while the drawer polls), expires 5 min after the
 * last interaction so an abandoned edit tab eventually frees the node. */
const EDIT_LOCK_LEASE_MS = 5 * 60 * 1000
export const lockedSurfaceClassName = 'pointer-events-none opacity-40 blur-[0.3px] select-none'

const hasToMillis = (value: unknown): value is { toMillis: () => number } => (
  Boolean(value) && typeof value === 'object' && typeof (value as { toMillis?: unknown }).toMillis === 'function'
)

const getLockExpiryMs = (lock: DocumentLock) => {
  const expiresAt = lock.expiresAt
  if (hasToMillis(expiresAt)) return expiresAt.toMillis()
  if (expiresAt instanceof Date) return expiresAt.getTime()
  if (typeof expiresAt === 'number') return expiresAt
  return 0
}

const normalizeLock = (id: string, data: Partial<DocumentLock>): DocumentLock | null => {
  if (
    typeof data.workspaceId !== 'string' ||
    typeof data.lockedBy !== 'string' ||
    typeof data.lockedByName !== 'string'
  ) {
    return null
  }

  return {
    id,
    workspaceId: data.workspaceId,
    lockedBy: data.lockedBy,
    lockedByName: data.lockedByName,
    expiresAt: data.expiresAt,
  }
}

export const usePessimisticLock = () => {
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const { activeWorkspaceId, activeRole } = useWorkspace()
  const isHelper = isHelperOnlyAccount({ userProfile, currentUser, activeRole }) || activeRole === 'HELPER'
  const [locks, setLocks] = useState<DocumentLock[]>([])

  useEffect(() => {
    if (!activeWorkspaceId || isHelper || isNoSignInPreviewActive()) return

    const locksQuery = query(
      collection(firestoreDb, 'workspaces', activeWorkspaceId, 'locks'),
    )

    return onSnapshot(
      locksQuery,
      (snapshot) => {
        const now = Date.now()
        setLocks(
          snapshot.docs
            .map((snapshotDocument) =>
              normalizeLock(snapshotDocument.id, snapshotDocument.data() as Partial<DocumentLock>),
            )
            .filter((lock): lock is DocumentLock => lock !== null)
            .filter((lock) => getLockExpiryMs(lock) > now),
        )
      },
      (error) => {
        console.warn('[usePessimisticLock] locks listener failed', error)
        setLocks([])
      },
    )
  }, [activeWorkspaceId, isHelper])

  const activeLocks = useMemo(
    () => ((isHelper || isNoSignInPreviewActive()) ? EMPTY_LOCKS : locks.filter((lock) => lock.workspaceId === activeWorkspaceId)),
    [activeWorkspaceId, isHelper, locks],
  )

  const acquireLease = useCallback(
    async (elementId: string) => {
      if (isHelper || !activeWorkspaceId || !currentUser) return

      await setDoc(doc(firestoreDb, 'workspaces', activeWorkspaceId, 'locks', elementId), {
        workspaceId: activeWorkspaceId,
        lockedBy: currentUser.uid,
        lockedByName: currentDisplayName || currentUser.email || 'Co-worker',
        expiresAt: Timestamp.fromMillis(Date.now() + LOCK_LEASE_MS),
      })
    },
    [activeWorkspaceId, currentDisplayName, currentUser, isHelper],
  )

  const releaseLease = useCallback(async (elementId: string) => {
    if (isHelper || !activeWorkspaceId) return
    const lockCollection = collection(firestoreDb, 'workspaces', activeWorkspaceId, 'locks')
    const inherited = await getDocs(query(lockCollection, where('rootLockId', '==', elementId)))
    if (inherited.empty) {
      await deleteDoc(doc(lockCollection, elementId))
      return
    }
    const batch = writeBatch(firestoreDb)
    inherited.docs.forEach((snapshot) => batch.delete(snapshot.ref))
    await batch.commit()
  }, [activeWorkspaceId, isHelper])

  /** Acquire (or renew) a 5-minute edit-session lease. Used by the node detail
   * drawer's Edit toggle. Same doc shape as acquireLease, longer expiry. */
  const acquireEditLease = useCallback(
    async (elementId: string, options?: { override?: boolean }) => {
      if (isHelper || !activeWorkspaceId || !currentUser) return
      if (options?.override && !roleCanPerform(activeRole, 'ADMIN')) throw new Error('Only workspace admins can override an edit lock.')
      const elementSnapshot = await getDocs(
        query(
          collection(firestoreDb, 'elements'),
          where('workspaceId', '==', activeWorkspaceId),
          ...scopedReadConstraints(activeWorkspaceId),
        ),
      )
      const elements = elementSnapshot.docs.map((snapshot) => ({ id: snapshot.id, parentId: snapshot.data().parentId as string | null | undefined }))
      const targetIds = new Set([elementId])
      let changed = true
      while (changed) {
        changed = false
        elements.forEach((element) => {
          if (element.parentId && targetIds.has(element.parentId) && !targetIds.has(element.id)) {
            targetIds.add(element.id)
            changed = true
          }
        })
      }
      const lockRefs = [...targetIds].map((id) => doc(firestoreDb, 'workspaces', activeWorkspaceId, 'locks', id))
      const now = Date.now()
      await runTransaction(firestoreDb, async (transaction) => {
        const snapshots = await Promise.all(lockRefs.map((lockRef) => transaction.get(lockRef)))
        const conflict = snapshots.find((snapshot) => {
          if (!snapshot.exists()) return false
          const data = snapshot.data() as Partial<DocumentLock>
          return data.lockedBy !== currentUser.uid && getLockExpiryMs({ id: snapshot.id, workspaceId: activeWorkspaceId, lockedBy: data.lockedBy ?? '', lockedByName: data.lockedByName ?? 'Co-worker', expiresAt: data.expiresAt }) > now
        })
        if (conflict && !options?.override) {
          const conflictData = conflict.data() as Partial<DocumentLock> | undefined
          throw new Error(`${conflictData?.lockedByName || 'A teammate'} is editing`)
        }
        lockRefs.forEach((lockRef) => transaction.set(lockRef, {
          workspaceId: activeWorkspaceId,
          elementId: lockRef.id,
          rootLockId: elementId,
          lockedBy: currentUser.uid,
          lockedByName: currentDisplayName || currentUser.email || 'Co-worker',
          expiresAt: Timestamp.fromMillis(now + EDIT_LOCK_LEASE_MS),
        }))
      })
    },
    [activeRole, activeWorkspaceId, currentDisplayName, currentUser, isHelper],
  )

  const externalLocks = useMemo(
    () => activeLocks.filter((lock) => lock.lockedBy !== currentUser?.uid),
    [activeLocks, currentUser?.uid],
  )

  const getExternalLock = useCallback(
    (targetId: string) => externalLocks.find((lock) => lock.id === targetId) ?? null,
    [externalLocks],
  )

  const isLockedByExternalUser = useCallback(
    (targetId: string) => Boolean(getExternalLock(targetId)),
    [getExternalLock],
  )

  const getLockedSurfaceClassName = useCallback(
    (targetId: string) => isLockedByExternalUser(targetId) ? lockedSurfaceClassName : '',
    [isLockedByExternalUser],
  )

  const getLockBadgeLabel = useCallback((targetId: string) => {
    const lock = getExternalLock(targetId)
    return lock ? `Locked by ${lock.lockedByName}` : ''
  }, [getExternalLock])

  return {
    locks: activeLocks,
    externalLocks,
    acquireLease,
    acquireEditLease,
    releaseLease,
    getExternalLock,
    isLockedByExternalUser,
    getLockedSurfaceClassName,
    getLockBadgeLabel,
  }
}
